/* eslint-disable roros/no-raw-html-in-routes */
// reason: pre-existing <button>/<input>/<select> elements that pre-date the LINT-1 (AILLM-3) rule extension. New code must use components/ui/*. Migration tracked separately.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrgStore } from '../../stores/orgStore';
import { Badge } from '../../components/ui/Badge/Badge';
import { usePageHeader } from '@/hooks/usePageHeader';
import { PageContainer } from '../../components/ui/PageContainer/PageContainer';
import { StatCard } from '@/components/ui/StatCard';
import { cn } from '@/lib/utils';
import { apiFetch } from '@/lib/api';
import { formatDate } from '@/lib/localeFormatters';
import { t } from '@/lib/i18n';

interface OrgStats {
  totalMembers: number;
  pendingInvites: number;
  monthlyUsage: {
    aiGenerations: number;
  };
}

interface QuickAction {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  color: string;
  comingSoon?: boolean;
}

export function OrgOverview() {
  const activeOrg = useOrgStore(s => s.orgs.find(org => org.id === s.activeOrgId));

  usePageHeader({ title: t('Organization'), breadcrumbs: [] });

  const [stats, setStats] = useState<OrgStats>({
    totalMembers: 0,
    pendingInvites: 0,
    monthlyUsage: {
      aiGenerations: 0,
    }
  });
  const [recentActivity, setRecentActivity] = useState<Array<{ id: string; type: string; description: string; timestamp: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!activeOrg?.id) return;
      setLoading(true);
      try {
        const [statsRes, actRes] = await Promise.all([
          apiFetch(`/api/orgs/${encodeURIComponent(activeOrg.id)}/dashboard/stats`, { headers: { 'X-Organization-Id': activeOrg.id } }),
          apiFetch(`/api/orgs/${encodeURIComponent(activeOrg.id)}/dashboard/activity`, { headers: { 'X-Organization-Id': activeOrg.id } }),
        ]);
        const statsJson = await statsRes.json();
        const actJson = await actRes.json();
        if (statsRes.ok && statsJson?.success && statsJson.data) setStats(statsJson.data as OrgStats);
        if (actRes.ok && actJson?.success && Array.isArray(actJson.data)) setRecentActivity(actJson.data);
      } catch (_err) {
        // silent; keep defaults
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeOrg?.id]);

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const absMs = Math.abs(diffInMs);
    const mins = Math.floor(absMs / (1000 * 60));
    const hours = Math.floor(absMs / (1000 * 60 * 60));
    const days = Math.floor(absMs / (1000 * 60 * 60 * 24));
    const suffix = diffInMs >= 0 ? t('ago') : t('from now');
    if (mins < 60) return `${mins} ${t('minutes')} ${suffix}`;
    if (hours < 24) return `${hours} ${t('hours')} ${suffix}`;
    return `${days} ${t('days')} ${suffix}`;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'invite_sent':
        return (
          <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
        );
      case 'member_joined':
        return (
          <div className="w-10 h-10 bg-secondary-50 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87" />
            </svg>
          </div>
        );
      case 'asset_created':
        return (
          <div className="w-10 h-10 bg-secondary-50 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2z" />
            </svg>
          </div>
        );
      case 'gallery_uploaded':
      case 'attachment_uploaded':
        return (
          <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
          </div>
        );
      case 'brand_color_saved':
        return (
          <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
          </div>
        );
      case 'post_scheduled':
        return (
          <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
        );
      case 'template_created':
        return (
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
        );
    }
  };

  const quickActions: QuickAction[] = [
    {
      title: t('Manage Team'),
      description: t('Invite members and manage roles'),
      href: '/organization/members',
      color: 'bg-primary-50 text-primary-600 border-primary-200',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 110 7.75M8 3.13a4 4 0 110 7.75" />
        </svg>
      )
    },
    // Attachments quick action hidden — see
    // docs/plans/archived/2026-04-15-hide-attachments-chat-notifications.md
    // Replaced Brand Guidelines with Organization Info and Company Knowledge cards below
    {
      title: t('Organization Info'),
      description: t('View and edit organization information'),
      href: '/organization/about',
      color: 'bg-muted text-muted-foreground border-border',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h18M3 17h18" />
        </svg>
      )
    },
    {
      title: t('Company Knowledge'),
      description: t('Manage knowledge documents and search'),
      href: '/organization/knowledge',
      color: 'bg-muted text-muted-foreground border-border',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17a4 4 0 100-8 4 4 0 000 8zM21 21l-4.35-4.35" />
        </svg>
      )
    },
    {
      title: t('AI Settings'),
      description: t('Configure AI generation preferences'),
      href: '/organization/ai-settings',
      color: 'bg-muted text-muted-foreground/60 border-border',
      comingSoon: true,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: t('Billing'),
      description: t('Manage subscription and usage'),
      href: '/organization/billing',
      color: 'bg-muted text-muted-foreground/60 border-border',
      comingSoon: true,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      )
    }
  ];

  if (!activeOrg) {
    return (
      <PageContainer>
        <div className="text-center">
          <div className="text-sm text-muted-foreground">No organization selected</div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
        {/* Organization Details */}
        <div className="bg-card rounded-lg border border-border p-6 mb-8">
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Organization ID</dt>
              <dd className="text-sm text-foreground font-mono">{activeOrg.slug}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Plan</dt>
              <dd className="text-sm text-foreground">{activeOrg.plan_key || 'Free'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="text-sm text-foreground">{activeOrg.created_at ? formatDate(activeOrg.created_at) : 'N/A'}</dd>
            </div>
          </dl>
        </div>

        {/* Stats Overview */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-foreground mb-4">Organization Stats</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard
            label={t('Team Members')}
            value={stats.totalMembers}
            trend={stats.pendingInvites > 0 ? `${stats.pendingInvites} ${t('pending')}` : undefined}
            iconColor="primary"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87" />
              </svg>
            }
          />

          {/* Attachments stat card hidden — see
              docs/plans/archived/2026-04-15-hide-attachments-chat-notifications.md */}

          <StatCard
            label={t('Example Placeholder')}
            value="—"
            trend={t('Coming soon')}
            iconColor="muted"
            className="opacity-70"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            }
          />

          <StatCard
            label={t('AI Generations')}
            value={stats.monthlyUsage.aiGenerations}
            trend={t('this month')}
            iconColor="default"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            }
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quickActions.map((action) => {
            if (action.comingSoon) {
              return (
                <div
                  key={action.href}
                  className="block bg-card rounded-lg border p-6 transition-all opacity-60 cursor-not-allowed"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 ${action.color} rounded-lg flex items-center justify-center`}>
                      {action.icon}
                    </div>
                    <Badge variant="outline" className="text-xs">Coming Soon</Badge>
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{action.title}</h3>
                  <p className="text-sm text-muted-foreground">{action.description}</p>
                </div>
              );
            }

            return (
              <Link
                key={action.href}
                to={action.href}
                className="block bg-card rounded-lg border p-6 transition-all hover:shadow-md hover:border-border"
              >
                <div className="flex items-start gap-4">
                  <div className={cn('p-3 rounded-lg border', action.color)}>
                    {action.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-foreground mb-1">
                      {action.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{action.description}</p>
                  </div>
                  <div className="text-muted-foreground">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h2>
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="space-y-4">
            {recentActivity.length === 0 && !loading && (
              <div className="text-sm text-muted-foreground">No recent activity.</div>
            )}
            {recentActivity.map((a) => (
              <div key={a.id} className="flex items-center gap-4 pb-4 last:pb-0 border-b last:border-b-0 border-border">
                {getActivityIcon(a.type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{a.description}</p>
                  <p className="text-xs text-muted-foreground mt-1">{formatTimeAgo(new Date(a.timestamp))}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-border">
            <button className="w-full text-sm text-muted-foreground hover:text-foreground py-2">
              View all activity
              <span className="ml-2 text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                Coming Soon
              </span>
            </button>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
